import React, { useEffect, useState } from 'react';

interface PaymentHistoryProps {
  documentId: string;
  currencyCode: string;
  onPaymentDeleted?: () => void;
  fetchPayments: (documentId: string) => Promise<any[]>;
  deletePayment: (paymentId: string, documentId: string) => Promise<void>;
  paymentsLoading: string | null;
  deletePaymentLoading: string | null;
  /** Bump from the parent (e.g. after recording a payment) to force a re-fetch
   *  so the open view never shows a stale payment list / balance. */
  reloadKey?: number | string;
}

interface PaymentRow {
  id: string;
  amount: number;
  date: string;
  reference?: string;
  method?: string;
  bankReconciliationId?: string;
  bankTransactionId?: string;
}

export function PaymentHistory({
  documentId,
  currencyCode,
  onPaymentDeleted,
  fetchPayments,
  deletePayment,
  paymentsLoading,
  deletePaymentLoading,
  reloadKey,
}: PaymentHistoryProps) {
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mapRows = (list: any[]): PaymentRow[] => (list || []).map((p: any) => ({
    id: p.PaymentID || p.id,
    amount: p.Amount ?? p.amount,
    date: p.Date || p.date,
    reference: p.Reference || p.reference,
    method: p.Account?.Name || p.method,
    bankReconciliationId: p.bankReconciliationId,
    bankTransactionId: p.bankTransactionId,
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchPayments(documentId);
      if (!cancelled) setPayments(mapRows(list));
    })();
    return () => { cancelled = true; };
    // reloadKey lets the parent force a refresh (e.g. after a new payment).
  }, [documentId, fetchPayments, reloadKey]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: currencyCode || 'AUD' }).format(value);

  const formatDate = (value: string) => {
    try {
      return new Date(value + 'Z').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return value;
    }
  };

  const handleDelete = async (paymentId: string) => {
    if (!window.confirm('Remove this payment? This cannot be undone.')) return;
    setError(null);
    try {
      await deletePayment(paymentId, documentId);
      // Re-fetch from the server (source of truth) rather than optimistically
      // dropping the row, so the list + parent balance reflect reality.
      const list = await fetchPayments(documentId);
      setPayments(mapRows(list));
      onPaymentDeleted?.();
    } catch (err: any) {
      // Surface the failure instead of swallowing it — the user must not think
      // a payment was removed when it wasn't (audit: low).
      setError(err?.message || 'Failed to remove payment. Please try again.');
    }
  };

  const loading = paymentsLoading === documentId;

  if (loading && payments.length === 0) {
    return (
      <div className="mt-3 pt-3 border-top">
        <span className="text-muted small">Payment history</span>
        <div className="d-flex align-items-center gap-2 mt-1">
          <span className="spinner-border spinner-border-sm text-primary" role="status" />
          <span className="text-muted small">Loading…</span>
        </div>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="mt-3 pt-3 border-top">
        <span className="text-muted small">Payment history</span>
        <p className="text-muted small mt-1 mb-0">No payments recorded</p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-top">
      <span className="text-muted small d-block mb-2">Payment history</span>
      {error && <div className="alert alert-danger py-1 px-2 small mb-2">{error}</div>}
      <ul className="list-unstyled mb-0">
        {payments.map((p) => (
          <li
            key={p.id}
            className="d-flex justify-content-between align-items-center py-2 border-bottom border-gray-200"
          >
            <div>
              <span className={`fw-semibold ${p.amount < 0 ? 'text-danger' : ''}`}>
                {formatCurrency(p.amount)}
              </span>
              <span className="text-muted small ms-2">
                {formatDate(p.date)}
                {p.reference && ` · ${p.reference}`}
                {p.method && ` · ${p.method}`}
              </span>
            </div>
            {/* A refund is a NEGATIVE row. Deleting it would run
                amountPaid − (−R) = amountPaid + R and leave the invoice
                over-paid, so it is never deletable from here — reverse it at
                the source (Stripe) instead. */}
            {p.bankReconciliationId ? (<a className="badge badge-light-success" href={`/cashflow?view=reconcile&transaction=${encodeURIComponent(p.bankTransactionId || '')}`}>Bank reconciled</a>) : p.amount < 0 ? (
              <span className="badge badge-light-danger fs-9" title="Refund — reverse this in Stripe, not here">
                Refund
              </span>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light-danger"
                onClick={() => handleDelete(p.id)}
                disabled={deletePaymentLoading === p.id}
                title="Remove payment"
              >
                {deletePaymentLoading === p.id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <i className="bi bi-trash" />
                )}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PaymentHistory;
